import React, { useState, useEffect } from 'react';

import { useAuth } from '@/src/app/context/useAuth';
import { type Option, getZeroOption } from '@/src/utils';
import { ErrorMessage, Loading, SimpleSingleSelect } from '@/src/ui';
import { apiRequest } from '@/src/services';
import type {
    FilterDTO,
    StudentDTO,
    StudentHandscoreDTO,
    ExamAssignmentDTO,
} from './TeacherDashboardDTOs';
import CollapsiblePanel from '../_shared/CollapsiblePanel';
import StudentsTable from './components/StudentsTable';
import HandscoreItemsTable from './components/HandscoreItemsTable';
import ExamAssignmentsTable from './components/ExamAssignmentsTable';
import CompletionStatusReportModal from '../../../exams/examAssignments/components/CompletionStatusReportModal';

const thumbnails = import.meta.glob('@/src/assets/thumbnails/*.{png,jpg,jpeg,svg}', {
    eager: true,
    import: 'default',
}) as Record<string, string>;

const loadingOption = getZeroOption('Loading...');
const selectClassOption = getZeroOption('Select a class...');
const noClassesOption = getZeroOption('No classes found');

const TeacherDashboard: React.FC = () => {
    document.title = 'Dashboard';
    const { user } = useAuth();

    const [loading, setLoading] = useState<boolean>(false);
    const [warning, setWarning] = useState<string>('');
    const [error, setError] = useState<string>('');

    const [selectedClassId, setSelectedClassId] = useState<number>(0);
    const [classFilters, setClassFilters] = useState<Option[]>([loadingOption]);

    const [studentData, setStudentData] = useState<StudentDTO[]>([]);
    const [handscoreItems, setHandscoreItems] = useState<StudentHandscoreDTO[]>([]);
    const [examAssignments, setExamAssignments] = useState<any[]>([]);
    const [showCompletionStatusReportModal, setShowCompletionStatusReportModal] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState<ExamAssignmentDTO | null>(null);

    useEffect(() => {
        (async function init() {
            setLoading(true);
            try {
                const handscoreRes = await apiRequest<StudentHandscoreDTO[]>(
                    'GET',
                    `assessments/studentExamAttempts/handscoreItemsOutstanding`
                );
                setHandscoreItems(handscoreRes.data || []);

                const examAssignmentsRes = await apiRequest<Array<ExamAssignmentDTO>>(
                    'GET',
                    `assessments/examAssignments`
                );
                setExamAssignments(examAssignmentsRes.data || []);

                const filtersRes = await apiRequest<FilterDTO>(
                    'GET',
                    'utility/filterData?getClass=true'
                );
                const newFilters = (filtersRes.data?.classOptions || []).slice(0, 1000);
                if (newFilters.length === 1000) {
                    setWarning('Only the first 1000 classes are shown.');
                }

                if (newFilters.length === 0) {
                    setWarning('No classes found.');
                    setClassFilters([noClassesOption]);
                } else {
                    const newClassFilters = [selectClassOption, ...newFilters];
                    setClassFilters(newClassFilters);
                    if (newClassFilters.length > 1) {
                        // Early loading reset to allow data to populate
                        setLoading(false);
                        setSelectedClassId(newClassFilters[1].id as number);
                    }
                }
            } catch {
                setError('Failed to load class filters.');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            if (loading || selectedClassId === 0) return;
            setLoading(true);
            setError('');
            try {
                const studentRes = await apiRequest<StudentDTO[]>(
                    'GET',
                    `dbo/users/studentsByClass?classId=${selectedClassId}`
                );
                setStudentData(studentRes.data || []);
            } catch {
                setError('Failed to load dashboard data.');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClassId]);

    const allGrades = React.useMemo(() => {
        const primary = user?.gradeShortCode;
        const alternates = user?.alternateGradeShortCodes || [];
        const gradesSet = new Set([primary, ...alternates].filter((g): g is string => !!g));
        return Array.from(gradesSet).sort((a, b) => {
            if (a.toUpperCase() === 'K') return -1;
            if (b.toUpperCase() === 'K') return 1;
            return a.localeCompare(b, undefined, { numeric: true });
        });
    }, [user]);

    // --- Dynamic Sizing Logic ---
    // If 1-3 books: Use ~33% width (Large)
    // If 4 books: Use ~25% width (Medium)
    // If 5+ books: Use ~16% width (Small/Standard)
    const getBookSize = (count: number) => {
        if (count <= 3) return 'calc(33.333% - 12px)';
        if (count === 4) return 'calc(25% - 12px)';
        return 'calc(16.666% - 12px)';
    };

    const bookFlexBasis = getBookSize(allGrades.length);

    const studentsTitle = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Students</span>
        </div>
    );

    const examAssignmentsTitle = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Assessments</span>
        </div>
    );

    const handscoreItemsTitle = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Handscore Items</span>
        </div>
    );

    const teacherEbookTitle = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>Teacher Edition eBooks</span>
        </div>
    );

    return (
        <>
            <h1 className="page-header">Classroom Dashboard</h1>

            {showCompletionStatusReportModal && selectedAssignment && (
                <CompletionStatusReportModal
                    open={showCompletionStatusReportModal}
                    examAssignmentId={selectedAssignment.id}
                    examName={selectedAssignment.examName}
                    classIds={selectedAssignment.classes?.map((c) => c.classId) ?? []}
                    onClose={() => {
                        setShowCompletionStatusReportModal(false);
                        setSelectedAssignment(null);
                    }}
                />
            )}

            <Loading visible={loading} />
            <ErrorMessage error={error} />
            <ErrorMessage error={warning} color="yellow" />

            <div className="my-6" />

            <div
                style={{
                    display: 'flex',
                    width: '100%',
                    gap: '24px',
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                }}
            >
                {/* Left column */}
                <div
                    style={{
                        flex: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: '350px',
                        gap: '16px',
                    }}
                >
                    <CollapsiblePanel title={studentsTitle} headerTextColor="var(--pm-primary)">
                        <div style={{ marginBottom: '16px' }}>
                            <SimpleSingleSelect
                                name="class"
                                label="Select Class"
                                disabled={loading}
                                options={classFilters}
                                value={selectedClassId}
                                sx={{ width: 250 }}
                                onChange={(e) => setSelectedClassId(e.target.value as number)}
                            />
                        </div>
                        <StudentsTable data={studentData} />
                    </CollapsiblePanel>

                    <CollapsiblePanel
                        title={examAssignmentsTitle}
                        headerTextColor="var(--pm-primary)"
                        link="/exams/examAssignments"
                        tooltip="View and manage assessments"
                    >
                        <ExamAssignmentsTable
                            data={examAssignments}
                            setShowCompletionStatusReportModal={setShowCompletionStatusReportModal}
                            setSelectedAssignment={setSelectedAssignment}
                        />
                    </CollapsiblePanel>
                </div>

                {/* Right column */}
                <div
                    style={{
                        flex: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: '350px',
                        gap: '16px',
                    }}
                >
                    {allGrades.length > 0 && (
                        <CollapsiblePanel
                            title={teacherEbookTitle}
                            headerTextColor="var(--pm-primary)"
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    gap: '12px',
                                    padding: '12px',
                                    width: '100%',
                                }}
                            >
                                {allGrades.map((gradeCode) => {
                                    const gradeToUpper = gradeCode.toUpperCase();
                                    const thumbKey = `/src/assets/thumbnails/cover-grade-${gradeToUpper}.jpg`;
                                    const thumbSrc = thumbnails[thumbKey];

                                    if (!thumbSrc) return null;

                                    const volumes =
                                        gradeToUpper === 'K'
                                            ? [['1'], ['2']]
                                            : [
                                                  ['1A', '1B'],
                                                  ['2A', '2B'],
                                              ];
                                    const baseUrl = `https://teacher-files.paradigmmath.com/EN/_flipbook/TE`;

                                    return (
                                        <div
                                            key={gradeCode}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                flex: `0 0 ${bookFlexBasis}`,
                                                minWidth: '80px',
                                                marginBottom: '8px',
                                                gap: '6px',
                                            }}
                                        >
                                            <img
                                                src={thumbSrc}
                                                alt={`Grade ${gradeCode} Teacher eBook`}
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    objectFit: 'contain',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                                }}
                                            />
                                            <span style={{ fontSize: '0.75rem', color: '#666' }}>
                                                Grade {gradeToUpper}
                                            </span>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    justifyContent: 'center',
                                                    gap: '4px',
                                                }}
                                            >
                                                {volumes.map((row, rowIndex) => (
                                                    <div
                                                        key={rowIndex}
                                                        style={{ display: 'flex', gap: '4px' }}
                                                    >
                                                        {row.map((vol) => (
                                                            <a
                                                                key={vol}
                                                                href={`${baseUrl}/G${gradeToUpper}-TE-Vol-${vol}/index.html`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title={`Grade ${gradeToUpper} Vol ${vol}`}
                                                                style={{
                                                                    fontSize: '0.65rem',
                                                                    padding: '2px 6px',
                                                                    borderRadius: '999px',
                                                                    border: '1px solid var(--pm-primary)',
                                                                    color: 'var(--pm-primary)',
                                                                    textDecoration: 'none',
                                                                    lineHeight: '1.4',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                                onMouseOver={(e) =>
                                                                    Object.assign(
                                                                        e.currentTarget.style,
                                                                        {
                                                                            backgroundColor:
                                                                                'var(--pm-primary)',
                                                                            color: '#fff',
                                                                        }
                                                                    )
                                                                }
                                                                onMouseOut={(e) =>
                                                                    Object.assign(
                                                                        e.currentTarget.style,
                                                                        {
                                                                            backgroundColor:
                                                                                'transparent',
                                                                            color: 'var(--pm-primary)',
                                                                        }
                                                                    )
                                                                }
                                                            >
                                                                Vol {vol}
                                                            </a>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CollapsiblePanel>
                    )}

                    <CollapsiblePanel
                        title={handscoreItemsTitle}
                        headerTextColor="var(--pm-primary)"
                        link="/exams/handscoreQuestions"
                        tooltip="View handscore items"
                    >
                        <HandscoreItemsTable data={handscoreItems} />
                    </CollapsiblePanel>
                </div>
            </div>
        </>
    );
};

export default TeacherDashboard;
